/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import ReactMarkdown from 'react-markdown';

import { OverflowCarousel } from '@coursera-web/cds-labs';

import { isRightToLeft } from 'js/lib/language';

import { Button, Grid, Link, Typography2, breakpoints, typography2, useMediaQuery } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import type { MerchandisingModuleModuleType } from '@coursera/event-pulse-types';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import EnrollOrUpgradeToCourseraPlus from 'bundles/coursera-plus-common/components/subscriptionTiers/upgradeToPlus/EnrollOrUpgradeToCourseraPlus';
import { isCourseraPlusCheckoutFlowCtaTarget } from 'bundles/coursera-plus-common/utils/courseraPlusCollectionsUtils';
import SiteMiscExperiments from 'bundles/epic/clients/siteMisc';
import type { PromoTemplateCollectionsFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import Section from 'bundles/front-page/components/Section';
import useLohpCollection from 'bundles/front-page/hooks/useLohpCollection';
import CompactLearningProductCard from 'bundles/front-page/redesign-modules/spotlight-collections/CompactLearningProductCard';
import SpotlightCollectionPlaceholder from 'bundles/front-page/redesign-modules/spotlight-collections/SpotlightCollectionsModulePlaceholder';
import normalizeSpotlightCollectionsModuleContent from 'bundles/front-page/utils/normalizeSpotlightCollectionsModuleContent';

import _t from 'i18n!nls/front-page';

const MAX_PRODUCT_CARDS = 4;
const DEFAULT_LINEAR_GRADIENT_BACKGROUND =
  'linear-gradient(86deg, var(--cds-color-emphasis-primary-background-strong) 3.56%,var(--cds-color-emphasis-primary-background-default) 40.71%,var(--cds-color-green-300) 96.44%)';

export type SpotlightCollectionsModuleRendererProps =
  | {
      headline: string;
      description: string;
      ctaText: string;
      ctaTarget: string;
      collectionSets: {
        id: string;
        collectionModuleId: string;
        presetCollectionId?: string;
        filterName?: string;
      }[];
      linearGradientBackground: {
        angle: string;
        colors: string[];
        stops: number[];
      };
      content?: never;
      dataTestId?: string;
    }
  | {
      headline?: never;
      description?: never;
      ctaText?: never;
      ctaTarget?: never;
      collectionSets?: never;
      linearGradientBackground?: never;
      content: PromoTemplateCollectionsFragment;
      dataTestId?: string;
    };

const styles = {
  container: css`
    position: relative;
    padding: var(--cds-spacing-200);
    display: grid;
    grid-template-columns: 1fr 4fr;
    gap: var(--cds-spacing-400);

    ${breakpoints.down('md')} {
      grid-template-columns: 1fr;
      grid-template-rows: auto;
      gap: var(--cds-spacing-200);
    }

    ${breakpoints.up('lg')} {
      padding-left: var(--cds-spacing-400);
    }
  `,
  containerWithBackground: css`
    position: relative;
    margin: var(--cds-spacing-200) 0;
    padding: var(--cds-spacing-200) 0;
  `,
  sectionTitle: css`
    ${breakpoints.down('xs')} {
      ${typography2.subtitleMedium}
    }
  `,
  headingLink: css`
    color: var(--cds-color-neutral-primary-invert) !important;
  `,
  contentContainer: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;

    ${breakpoints.down('md')} {
      flex-direction: column;
      align-items: flex-start;
    }
  `,
  description: css`
    margin-top: var(--cds-spacing-50);
  `,
  descriptionLink: css`
    color: var(--cds-color-neutral-primary-invert) !important;
  `,
  cta: css`
    margin-top: var(--cds-spacing-150);
  `,
  ctaIconButton: css`
    padding: 0;

    ${breakpoints.down('md')} {
      &:hover {
        box-shadow: 0 0 0 1px var(--cds-color-neutral-primary-invert);
      }
    }
  `,
  collection: (isProductCardTest1: boolean) => css`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: var(--cds-spacing-150);
    width: 100%;

    ${breakpoints.down('xs')} {
      /* 600 / 4 = 150 */
      grid-template-columns: repeat(4, minmax(150px, 1fr));
      width: auto;
    }

    /* !important to override the styles set by CDS */
    .cds-ProductCard-grid {
      min-width: unset !important;
      ${isProductCardTest1 ? '' : 'border-radius: var(--cds-border-radius-100) !important'};
    }

    .cds-Avatar-base {
      width: 12px !important;
      height: 12px !important;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .cds-ProductCard-footer {
      gap: 0;
    }

    ${!isProductCardTest1 &&
    css`
      ${breakpoints.down('xs')} {
        .cds-CommonCard-title {
          ${typography2.subtitleSmall}
        }

        .cds-CommonCard-metadata,
        .cds-CommonCard-metadata p,
        .cds-ProductCard-partnerNames {
          ${typography2.bodyTertiary}
        }

        .cds-ProductCard-gridPreviewContainer .cds-CommonCard-previewImage {
          margin: calc(var(--cds-spacing-100) * -1) calc(var(--cds-spacing-100) * -1) var(--cds-spacing-100)
            calc(var(--cds-spacing-100) * -1) !important;
          border-radius: var(--cds-border-radius-100) var(--cds-border-radius-100) 0 0 !important;
        }
      }
    `}
  `,
  backgroundGradient: (background: string) => css`
    &::after {
      content: '';
      position: absolute;
      border-radius: var(--cds-border-radius-300);
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: ${background};
      background-position: top left;
      z-index: -1;

      ${breakpoints.down('xs')} {
        border-radius: unset;
      }
    }
  `,
  backgroundGradientRtl: css`
    &::after {
      transform: scaleX(-1);
    }
  `,
};

export const getLinearGradientBackground = (
  linearGradientBackground: SpotlightCollectionsModuleRendererProps['linearGradientBackground']
) => {
  const colorStopList = linearGradientBackground?.colors.map(
    (color, index) => `${color} ${linearGradientBackground?.stops[index]}%`
  );

  return `linear-gradient(${linearGradientBackground?.angle}, ${colorStopList})`;
};

const SpotlightCollectionsModuleRenderer = ({
  headline,
  description,
  ctaText,
  ctaTarget,
  collectionSets,
  linearGradientBackground,
  content,
  dataTestId,
}: SpotlightCollectionsModuleRendererProps) => {
  const isTabletOrSmaller = useMediaQuery(breakpoints.down('md'));
  const isMobile = useMediaQuery(breakpoints.down('xs'));
  const currentCollectionSetIndex = 0;
  const collectionIndex = currentCollectionSetIndex;
  const isRTL = isRightToLeft(_t.getLocale());
  const track = useTracker();

  let moduleHeadline;
  let moduleDescription;
  let moduleCtaText;
  let moduleCtaTarget;
  let moduleCollectionSets;
  let moduleLinearGradientBackground;

  if (content) {
    const normalizedContent = normalizeSpotlightCollectionsModuleContent(content);
    moduleHeadline = normalizedContent?.headline;
    moduleDescription = normalizedContent?.description;
    moduleCtaText = normalizedContent?.ctaText;
    moduleCtaTarget = normalizedContent?.ctaTarget;
    moduleCollectionSets = normalizedContent?.collectionSets;
    moduleLinearGradientBackground = normalizedContent?.gradientBackgroundColor || DEFAULT_LINEAR_GRADIENT_BACKGROUND;
  } else {
    moduleHeadline = headline;
    moduleDescription = description;
    moduleCtaText = ctaText;
    moduleCtaTarget = ctaTarget;
    moduleCollectionSets = collectionSets;
    moduleLinearGradientBackground = getLinearGradientBackground(linearGradientBackground);
  }

  const { collection, loading: isCollectionsLoading } = useLohpCollection({
    queryCollectionId: moduleCollectionSets?.[currentCollectionSetIndex]!.collectionModuleId ?? '',
    additionalInput: {
      preset_collection_id: moduleCollectionSets?.[currentCollectionSetIndex]!.presetCollectionId,
    },
  });

  const moduleEventingData = {
    moduleType: 'collections_module' as MerchandisingModuleModuleType,
    heading: moduleHeadline ?? '',
    body: moduleDescription ?? '',
  };

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_merchandising_module',
    {
      merchandisingModule: moduleEventingData,
    },
    [],
    { fullyVisible: true }
  );

  const trackClick = () => {
    track('click_button', {
      button: {
        name: 'view_all_products',
        linkURL: moduleCtaTarget ?? '',
        linkName: moduleCtaText ?? '',
        linkType: 'internal',
      },
    });
  };

  const customPrimaryCta = (
    <div css={styles.cta}>
      <EnrollOrUpgradeToCourseraPlus
        ctaLabel={moduleCtaText}
        ctaVariant="secondary"
        trackingName="spotlight_collections_primary_cta"
        shouldDisableIndiaExperiment
        ctaTarget={moduleCtaTarget}
        shouldUseHookForPostAuthCheckout
        ctaSize="small"
      />
    </div>
  );

  const renderCustomPrimaryCtaForCourseraPlus = moduleCtaTarget && isCourseraPlusCheckoutFlowCtaTarget(moduleCtaTarget);

  const renderPrimaryCtaOnDesktop = () => {
    if (isTabletOrSmaller) return null;
    if (renderCustomPrimaryCtaForCourseraPlus) {
      return customPrimaryCta;
    }

    if (moduleCtaText) {
      return (
        <Button
          variant="primaryInvert"
          size="small"
          component="a"
          href={moduleCtaTarget}
          css={styles.cta}
          onClick={trackClick}
          iconPosition="after"
          icon={<ArrowNextIcon size="medium" />}
          aria-label={`${moduleCtaText} - ${moduleHeadline}`}
        >
          {moduleCtaText}
        </Button>
      );
    }

    return null;
  };

  if (!moduleHeadline || !moduleCollectionSets || !moduleCollectionSets.length) return null;

  if (!isCollectionsLoading && !collection?.entities.length) {
    return null;
  }

  if (isCollectionsLoading) {
    return (
      <SpotlightCollectionPlaceholder
        numberOfCards={MAX_PRODUCT_CARDS}
        headline={moduleHeadline}
        ctaText={moduleCtaText}
      />
    );
  }

  const isProductCardTest1 = SiteMiscExperiments.preview('productCardTest1.1') !== 'control';

  const output = (
    <>
      <div css={styles.contentContainer}>
        {isTabletOrSmaller ? (
          <Link
            href={moduleCtaTarget}
            variant="quiet"
            icon={<ArrowNextIcon size="medium" />}
            iconPosition="after"
            css={styles.headingLink}
            onClick={trackClick}
            typographyVariant="subtitleLarge"
            aria-label={`${moduleCtaText} - ${moduleHeadline}`}
          >
            {moduleHeadline}
          </Link>
        ) : (
          <Typography2 variant="subtitleLarge" component="h2" color="invertBody" css={styles.sectionTitle}>
            {moduleHeadline}
          </Typography2>
        )}
        {moduleDescription && (
          <Typography2 variant="bodyPrimary" color="invertBody" component="div" css={styles.description}>
            <ReactMarkdown
              source={moduleDescription}
              renderers={{
                link: ({ href, children }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
                  <Link href={href} variant="standard" typographyVariant="inherit" css={styles.descriptionLink}>
                    {children}
                  </Link>
                ),
              }}
            />
          </Typography2>
        )}
        {renderPrimaryCtaOnDesktop()}
      </div>
      <OverflowCarousel css={styles.collection(isProductCardTest1)} shouldAllowSpaceForHoverItems>
        {collection?.entities
          .filter((entity) => !!entity.name)
          .slice(0, MAX_PRODUCT_CARDS)
          .map((entity, index) => (
            <CompactLearningProductCard
              entity={entity}
              entityIndex={index}
              collectionIndex={collectionIndex}
              collection={{
                ...collection,
                title: moduleHeadline,
                collectionTrackingData: {
                  id: moduleCollectionSets?.[currentCollectionSetIndex]!.id ?? '',
                },
              }}
              key={entity.id}
            />
          ))}
      </OverflowCarousel>
    </>
  );

  if (isMobile) {
    return (
      <Section
        css={[
          styles.containerWithBackground,
          styles.backgroundGradient(moduleLinearGradientBackground ?? ''),
          isRTL ? styles.backgroundGradientRtl : undefined,
        ]}
        ref={visibilityTrackingRef}
        dataTestId={dataTestId}
      >
        {output}
      </Section>
    );
  }

  return (
    <Section ref={visibilityTrackingRef} dataTestId={dataTestId}>
      <Grid
        item
        xs={12}
        css={[
          styles.container,
          styles.backgroundGradient(moduleLinearGradientBackground ?? ''),
          isRTL ? styles.backgroundGradientRtl : undefined,
        ]}
      >
        {output}
      </Grid>
    </Section>
  );
};

export default SpotlightCollectionsModuleRenderer;
